import { useLocation } from "react-router-dom";
import { ReactNode } from "react";

interface Props {
  children: ReactNode;
}

/**
 * La transición entre pantallas — y, sobre todo, la garantía de que la
 * aplicación NO puede quedarse en blanco por culpa de ella.
 *
 * EL FALLO QUE ARREGLA (reporte del usuario 27-08-2026, con captura): cabecera
 * bien, barra de abajo bien, y todo el centro liso, del color del fondo. Al
 * navegar un rato se arreglaba solo, y a veces se quedaba clavado. La captura
 * era la prueba: lo que faltaba encajaba EXACTAMENTE con lo que envuelve este
 * componente y con nada más. La cabecera y `MobileDock` se pintan fuera, y por
 * eso sobrevivían; y ni siquiera salía el `PageLoader`, porque también cuelga
 * de aquí dentro.
 *
 * Esto iba con `AnimatePresence mode="wait"` y un `motion.div` con
 * `initial={{ opacity: 0 }}`, o sea: la página entera empezaba INVISIBLE y solo
 * se veía cuando una animación de JavaScript terminaba de subirle la opacidad.
 * Dos maneras de quedarse a medias, y las dos dan la misma pantalla:
 *
 * 1. `mode="wait"` no monta la pantalla nueva hasta que la vieja TERMINA de
 *    irse. Si esa salida no termina, la nueva no llega a montarse nunca.
 * 2. La animación de entrada va por `requestAnimationFrame`. En el móvil, al
 *    volver de segundo plano —o con dos cambios de ruta seguidos, que aquí los
 *    hay: `ManagerGuard`, `OnboardingGate`, los `<Navigate>`— puede no llegar a
 *    correr. La opacidad se queda en 0 y con ella toda la pantalla.
 *
 * Y por eso el arreglo de ayer no sirvió de nada: aquí no falla nada, no se
 * lanza ningún error y el `ErrorBoundary` no tiene qué atrapar.
 *
 * AHORA la transición es CSS, y la diferencia que importa no es «CSS es más
 * rápido» sino el ESTADO EN REPOSO. `fill-mode-none` es la pieza: fuera de la
 * animación el elemento se queda con su estilo normal, que es visible. Si la
 * animación no llega a correr —la razón que sea— lo que se ve es la página, no
 * un hueco. La animación solo puede AÑADIR una entrada bonita; ya no puede
 * quitar la pantalla. Con `fill-mode: both`, que es lo que trae `animate-in`
 * por defecto, seguiría pudiendo: mantendría el primer fotograma (opacidad 0)
 * mientras no arranque, que es justo el fallo de arriba con otro disfraz.
 *
 * Se va también el `mode="wait"`: la pantalla nueva se monta ya, sin depender
 * de que la vieja acabe de irse. Se pierde la animación de salida —no se puede
 * animar algo que ya se desmontó— y a cambio la navegación no puede bloquearse.
 *
 * `key` por ruta para que la entrada se repita en cada pantalla, y
 * `motion-safe:` para respetar a quien pide menos animación en el sistema (lo
 * que antes se miraba a mano con `matchMedia`).
 */
export function PageTransition({ children }: Props) {
  const location = useLocation();

  return (
    <div
      key={location.pathname}
      className="w-full motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200 motion-safe:fill-mode-none"
    >
      {children}
    </div>
  );
}
